import { useEffect } from 'react';

const SITE_URL = 'https://vividh.lol';
const SITE_NAME = 'Vividh Mahajan';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

export interface PageMeta {
  /** Page title. The site name is appended unless this is the home page. */
  title: string;
  description: string;
  /** Path only, e.g. "/stateless". Used for canonical and og:url. */
  path: string;
  /** Absolute URL or root-relative path. Falls back to the site card. */
  image?: string;
  type?: 'website' | 'article';
  /** ISO date (YYYY-MM-DD) for article:published_time. */
  publishedTime?: string;
}

const setTag = (
  selector: string,
  create: () => HTMLElement,
  value: string | undefined
) => {
  const head = document.head;
  let el = head.querySelector<HTMLElement>(selector);

  if (!value) {
    el?.remove();
    return;
  }

  if (!el) {
    el = create();
    head.appendChild(el);
  }

  if (el instanceof HTMLMetaElement) {
    el.content = value;
  } else if (el instanceof HTMLLinkElement) {
    el.href = value;
  }
};

const meta = (name: string, value: string | undefined) =>
  setTag(
    `meta[name="${name}"]`,
    () => {
      const el = document.createElement('meta');
      el.setAttribute('name', name);
      return el;
    },
    value
  );

const property = (prop: string, value: string | undefined) =>
  setTag(
    `meta[property="${prop}"]`,
    () => {
      const el = document.createElement('meta');
      el.setAttribute('property', prop);
      return el;
    },
    value
  );

const absolute = (value: string) =>
  value.startsWith('http') ? value : `${SITE_URL}${value}`;

/**
 * Keeps the document head in sync with the current route.
 *
 * Note: this runs in the browser, so search engines that execute JS (Google)
 * and browser tabs get the right values. Social scrapers (X, LinkedIn, Slack)
 * read the static index.html and will show the site-level card until the build
 * prerenders routes.
 */
export const usePageMeta = ({
  title,
  description,
  path,
  image,
  type = 'website',
  publishedTime,
}: PageMeta) => {
  useEffect(() => {
    const fullTitle = title === SITE_NAME ? title : `${title} — ${SITE_NAME}`;
    const url = `${SITE_URL}${path}`;
    const imageUrl = image ? absolute(image) : DEFAULT_IMAGE;

    document.title = fullTitle;

    meta('description', description);
    setTag(
      'link[rel="canonical"]',
      () => {
        const el = document.createElement('link');
        el.rel = 'canonical';
        return el;
      },
      url
    );

    property('og:title', fullTitle);
    property('og:description', description);
    property('og:url', url);
    property('og:image', imageUrl);
    property('og:type', type);

    meta('twitter:title', fullTitle);
    meta('twitter:description', description);
    meta('twitter:url', url);
    meta('twitter:image', imageUrl);

    property('article:published_time', publishedTime);
  }, [title, description, path, image, type, publishedTime]);
};

export default usePageMeta;
